import { useMemo, useState } from 'react';
import { CheckCheck, Clock3, Inbox, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ticketsApi } from '@/api/endpoints';
import { Card } from '../components/ui';
import { TicketRow } from '../components/TicketRow';
import { adaptApiTicket, type DesignTicket } from '../lib/ticketMap';

type Filter = 'all' | 'open' | 'resolved';

function passFilter(t: DesignTicket, f: Filter) {
  if (f === 'all') return true;
  if (f === 'open') return t.status !== 'resolved' && t.status !== 'closed';
  return t.status === 'resolved' || t.status === 'closed';
}

export function TicketsListView() {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const { data: page } = useQuery({
    queryKey: ['tickets-list', 'all'],
    queryFn: () => ticketsApi.list({ limit: 200, sortBy: 'updatedAt', sortOrder: 'desc' }),
    staleTime: 30_000,
  });
  const all = useMemo(() => (page?.data || []).map(adaptApiTicket), [page]);
  const openCount = all.filter((t) => passFilter(t, 'open')).length;
  const resolvedCount = all.length - openCount;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((t) => passFilter(t, filter))
      .filter((t) => !q || t.title.toLowerCase().includes(q));
  }, [all, filter, search]);

  return (
    <div className="view active">
      <div className="page-hero">
        <h2>Мои обращения</h2>
        <p>Все ваши тикеты по поддержке софта и их текущий статус.</p>
      </div>
      <div className="stat-cards tickets-stat-grid">
        <Card className="stat-card reference-stat-card">
          <span>
            <Inbox size={14} />
            ОТКРЫТЫХ
          </span>
          <strong className="stat-orange">{openCount}</strong>
          <em>в работе у поддержки</em>
        </Card>
        <Card className="stat-card reference-stat-card">
          <span>
            <Clock3 size={14} />
            ВСЕГО
          </span>
          <strong className="stat-accent">{all.length}</strong>
          <em>за всё время</em>
        </Card>
        <Card className="stat-card reference-stat-card">
          <span>
            <CheckCheck size={14} />
            РЕШЕНО
          </span>
          <strong className="stat-green">{resolvedCount}</strong>
          <em>завершено</em>
        </Card>
      </div>
      <div className="ticket-workspace">
        <Card className="ticket-board-card">
          <div className="card-head">
            <h3>Список обращений</h3>
            <div className="filters">
              {([['all', 'Все'], ['open', 'Открытые'], ['resolved', 'Решённые']] as const).map(([id, label]) => (
                <button
                  key={id}
                  className={`chip ${filter === id ? 'active' : ''}`}
                  type="button"
                  onClick={() => setFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <label className="ticket-search-wrap">
            <Search size={19} />
            <input
              placeholder="Поиск по обращениям..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="ticket-list">
            {filtered.length ? (
              filtered.map((ticket, idx) => (
                <TicketRow ticket={ticket} key={ticket.id} highlight={idx === 0 && filter === 'all'} />
              ))
            ) : (
              <div className="empty">Здесь пока пусто</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
